import { FC, useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Box } from "@mui/material";

import { ITemplate, ITemplateRowBlockControl, RowControlType } from "@framework/types";
import { TextInput } from "@framework/mui-inputs-core";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";
import { useAsu } from "@framework/provider-asu";

import { capitalizeTest } from "../../../../../../../utils/text";
import { DroplistInput } from "../droplist";
import { EditIntInput } from "./edit-int";
import { DropdownInput, IAutocompleteOption } from "../dropdown";

interface IControlItems {
  [controlName: string]: Array<{ __val: string; "#text": string }>;
}

export interface IControlsInputProps {
  template: ITemplate | null;
  getTemplate: (templateId: number) => Promise<void>;
  isLoading: boolean;
  onValidationChange?: (validateFn: () => boolean) => void;
  onControlValueReset?: (controlName: string, value: any) => void;
}

export const ControlsInput: FC<IControlsInputProps> = props => {
  const { template, getTemplate, isLoading, onValidationChange, onControlValueReset } = props;

  const templateId = useWatch({ name: "templateId" });
  const formValues = useWatch();
  const { selectedItem, startTimestamp, endTimestamp } = useAsu();
  const [controlItems, setControlItems] = useState<IControlItems>({});
  const form = useFormContext();
  const lastLoadedDeps = useRef<Record<string, string>>({});

  const selectedItemId = selectedItem && ("Id" in selectedItem ? selectedItem.Id : selectedItem.TrainId);
  const selectedItemType = selectedItem && ("Id" in selectedItem ? 1 : 2);

  const { fn: loadControlItems, isLoading: isLoadingControlItems } = useApiCall(
    async (api, controlName: string, dependencies: Record<string, unknown>) => {
      if (!templateId) return [];

      return api.fetchJson({
        url: `/template/analytics/${templateId}/control/${controlName}`,
        data: {
          params: {
            idObject: selectedItemId,
            startDate: startTimestamp,
            endDate: endTimestamp,
            type: selectedItemType,
          },
          additionalParams: dependencies,
        },
      }) as Promise<Array<{ __val: string; "#text": string }>>;
    },
    { error: false, success: false },
  );

  const areDependenciesFilled = (dependencies?: string[]): boolean => {
    if (!dependencies || dependencies.length === 0) return true;

    return dependencies.every(dep => {
      const value = formValues[dep];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== undefined && value !== null && value !== "";
    });
  };

  const getUnfilledDependencies = (dependencies?: string[]): string[] => {
    if (!dependencies || dependencies.length === 0) return [];

    return dependencies.filter(dep => {
      const value = formValues[dep];
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return value === undefined || value === null || value === "";
    });
  };

  const getDependencyValues = (dependencies: string[]): Record<string, unknown> => {
    return dependencies.reduce(
      (acc, dep) => {
        acc[dep] = formValues[dep];
        return acc;
      },
      {} as Record<string, unknown>,
    );
  };

  const validateRequiredControls = (): boolean => {
    if (!template) return true;

    const controls = Array.isArray(template.control) ? template.control : [template.control];
    let isValid = true;

    controls.forEach(control => {
      if (control.__required) {
        const value = formValues[control.__name];
        const isEmpty = Array.isArray(value) ? value.length === 0 : !value || value === "";

        if (isEmpty) {
          form.setError(
            control.__name,
            {
              type: "required",
              message: "form.errors.required",
            },
            { shouldFocus: true },
          );
          isValid = false;
        } else {
          form.clearErrors(control.__name);
        }
      }
    });

    return isValid;
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateRequiredControls);
    }
  }, [formValues, template]);

  useEffect(() => {
    if (!template || !templateId) return;

    const controls = Array.isArray(template.control) ? template.control : [template.control];

    controls.forEach(control => {
      if (control.dependencies && control.dependencies.length > 0) {
        const filled = areDependenciesFilled(control.dependencies);

        if (filled) {
          const deps = getDependencyValues(control.dependencies);
          const depsKey = JSON.stringify(deps);

          if (lastLoadedDeps.current[control.__name] === depsKey) {
            return;
          }

          lastLoadedDeps.current[control.__name] = depsKey;

          void loadControlItems(undefined, control.__name, deps).then(
            (items: void | Array<{ __val: string; "#text": string }>) => {
              setControlItems(prev => ({
                ...prev,
                [control.__name]: items || [],
              }));

              const currentValue = form.getValues(control.__name);
              const isValueInNewItems = items?.some(item => item.__val === currentValue);

              if (currentValue && !isValueInNewItems) {
                const emptyValue = control.__type === RowControlType.multiselect ? [] : "";
                form.setValue(control.__name, emptyValue, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: false,
                });

                if (onControlValueReset) {
                  onControlValueReset(control.__name, emptyValue);
                }
              }
            },
          );
        } else {
          delete lastLoadedDeps.current[control.__name];
          setControlItems(prev => ({
            ...prev,
            [control.__name]: [],
          }));
          const emptyValue = control.__type === RowControlType.multiselect ? [] : "";
          form.setValue(control.__name, emptyValue, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });

          if (onControlValueReset) {
            onControlValueReset(control.__name, emptyValue);
          }
        }
      }
    });
  }, [formValues, template, templateId]);

  const renderControls = (control: ITemplateRowBlockControl) => {
    const hasDependencies = control.dependencies && control.dependencies.length > 0;
    const dependenciesFilled = areDependenciesFilled(control.dependencies);
    const isDisabled = hasDependencies && !dependenciesFilled;
    const unfilledDeps = hasDependencies ? getUnfilledDependencies(control.dependencies) : [];

    const items = hasDependencies && controlItems[control.__name] ? controlItems[control.__name] : control.item || [];

    const hasValidationError = !!form.formState.errors[control.__name];

    const helperText =
      !hasValidationError && isDisabled
        ? `Сначала заполните: ${unfilledDeps
            .map(dep => {
              const ctrl = Array.isArray(template?.control)
                ? template?.control.find(c => c.__name === dep)
                : template?.control?.__name === dep
                  ? template?.control
                  : null;
              return ctrl?.__label || dep;
            })
            .join(", ")}`
        : undefined;

    switch (control.__type) {
      case RowControlType.edit_int: {
        return <EditIntInput key={control.__name} control={control} disabled={isDisabled} />;
      }
      case RowControlType.edit_text: {
        return (
          <TextInput
            variant="outlined"
            key={control.__name}
            name={control.__name}
            label={control.__label}
            placeholder={control.__default || ""}
            disabled={isDisabled}
            helperText={helperText}
          />
        );
      }
      case RowControlType.droplist: {
        const options = items.reduce((opts: Record<string, string>, item) => {
          opts[item.__val] = capitalizeTest(item["#text"]);
          return opts;
        }, {});
        return (
          <DroplistInput
            key={control.__name}
            name={control.__name}
            label={control.__label}
            options={options}
            disabled={isDisabled}
            helperText={helperText}
          />
        );
      }
      case RowControlType.multiselect: {
        const options = items.reduce((opts: Record<string, string>, item) => {
          opts[item.__val] = capitalizeTest(item["#text"]);
          return opts;
        }, {});
        return (
          <DroplistInput
            key={control.__name}
            name={control.__name}
            label={control.__label}
            options={options}
            multiple
            disabled={isDisabled}
            helperText={helperText}
          />
        );
      }
      case RowControlType.dropdown: {
        const options = items.reduce((opts: Array<IAutocompleteOption>, item) => {
          const newItem: IAutocompleteOption = {
            id: item.__val,
            title: capitalizeTest(item["#text"]),
          };
          opts.push(newItem);
          return opts;
        }, []);
        return (
          <DropdownInput
            key={control.__name}
            name={control.__name}
            label={control.__label}
            options={options}
            disabled={isDisabled}
            helperText={helperText}
          />
        );
      }
      default:
        return null;
    }
  };

  useEffect(() => {
    if (templateId) {
      void getTemplate(templateId);
    }
  }, [templateId, selectedItemId, selectedItemType]);

  if (!templateId) {
    return null;
  }

  return (
    <ProgressOverlay isLoading={isLoading || isLoadingControlItems} wrapperSx={{ minHeight: 60, width: "100%" }}>
      {template?.control ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1,
          }}
        >
          {Array.isArray(template.control) ? (
            template.control.map(item => (
              <Box key={item.__name} sx={{ width: "100%", maxWidth: 300, pr: 3 }}>
                {renderControls(item)}
              </Box>
            ))
          ) : (
            <Box sx={{ width: "100%", maxWidth: 300 }}>{renderControls(template.control)}</Box>
          )}
        </Box>
      ) : null}
    </ProgressOverlay>
  );
};
